import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { Tutorial } from '../../types/tutorial';
import api from '../../utils/api';
import { Category } from '../../types/category';
import { CATEGORY_OPTIONS } from '../../types/category';

interface TutorialFormData {
  title: string;
  description: string;
  price: number;
  discount: number;
  category: Category;
  level: 'beginner' | 'medium' | 'advanced';
  imagePath: string;
  contentImagesPathes: string[];
  active: boolean;
  priority: number;
}

export const TutorialsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState<Tutorial | null>(null);
  const [formData, setFormData] = useState<TutorialFormData>({
    title: '',
    description: '',
    price: 0,
    discount: 0,
    category: CATEGORY_OPTIONS[0].value,
    level: 'beginner',
    imagePath: '',
    contentImagesPathes: [],
    active: true,
    priority: 0,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const response = await api.get<{ data: Tutorial[] }>('/tutorials/all');
      setTutorials(response.data.data);
    } catch (error) {
      console.error('Error fetching tutorials:', error);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (tutorial?: Tutorial) => {
    if (tutorial) {
      setEditingTutorial(tutorial);
      setFormData({
        ...tutorial,
        active: tutorial.active ?? true,
        priority: tutorial.priority ?? 0,
        contentImagesPathes: tutorial.contentImagesPathes ?? []
      });
    } else {
      setEditingTutorial(null);
      setFormData({
        title: '',
        description: '',
        price: 0,
        discount: 0,
        category: CATEGORY_OPTIONS[0].value,
        level: 'beginner',
        imagePath: '',
        contentImagesPathes: [],
        active: true,
        priority: 0,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingTutorial(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.contentImagesPathes.length === 0) {
      setImageError(t('admin.tutorials.atLeastOneImageRequired'));
      return;
    }
    try {
      const url = editingTutorial
        ? `/tutorials/${editingTutorial._id}`
        : '/tutorials';
      const method = editingTutorial ? 'put' : 'post';

      await api[method]<{ data: Tutorial }>(url, formData);
      fetchTutorials();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving tutorial:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('admin.tutorials.confirmDelete'))) {
      try {
        await api.delete(`/tutorials/${id}`);
        fetchTutorials();
      } catch (error) {
        console.error('Error deleting tutorial:', error);
      }
    }
  };

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>, type: 'image' | 'content') => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0], type);
    }
  }, []);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'content') => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0], type);
    }
  }, []);

  const handleFileUpload = async (file: File, type: 'image' | 'content') => {
    if (!file) return;

    try {
      setIsUploading(true);
      setImageError('');
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        const base64String = reader.result as string;
        
        try {
          const response = await api.post<{ url: string }>('/files/upload', {
            image: base64String
          });

          if (response.data && response.data.url) {
            if (type === 'image') {
              setFormData(prev => ({ 
                ...prev, 
                imagePath: response.data.url 
              }));
            } else {
              setFormData(prev => ({ 
                ...prev, 
                contentImagesPathes: [...prev.contentImagesPathes, response.data.url]
              }));
            }
          } else {
            throw new Error('No file URL received from server');
          }
        } catch (error: any) {
          console.error('Error uploading file:', error);
          setImageError(error.response?.data?.error || t('admin.tutorials.uploadFailed'));
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        setImageError(t('admin.tutorials.uploadFailed'));
        setIsUploading(false);
      };
    } catch (error) {
      console.error('Error processing file:', error);
      setImageError(t('admin.tutorials.uploadFailed'));
      setIsUploading(false);
    }
  };

  const handleDeleteContentImage = (index: number) => {
    if (formData.contentImagesPathes.length <= 1) {
      setImageError(t('admin.tutorials.atLeastOneImageRequired'));
      return;
    }
    setFormData(prev => ({
      ...prev,
      contentImagesPathes: prev.contentImagesPathes.filter((_, i) => i !== index)
    }));
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">{t('admin.tabs.tutorials')}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          {t('admin.tutorials.addNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.tutorials.title')}</TableCell>
              <TableCell>{t('admin.tutorials.category')}</TableCell>
              <TableCell>{t('admin.tutorials.level')}</TableCell>
              <TableCell>{t('admin.tutorials.price')}</TableCell>
              <TableCell>{t('admin.tutorials.discount')}</TableCell>
              <TableCell>{t('admin.tutorials.active')}</TableCell>
              <TableCell>{t('admin.tutorials.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tutorials
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((tutorial) => (
                <TableRow key={tutorial._id}>
                  <TableCell>{tutorial.title}</TableCell>
                  <TableCell>{tutorial.category}</TableCell>
                  <TableCell>{tutorial.level}</TableCell>
                  <TableCell>{tutorial.price}</TableCell>
                  <TableCell>{tutorial.discount}%</TableCell>
                  <TableCell>{tutorial.active ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenModal(tutorial)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(tutorial._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={tutorials.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTutorial ? t('admin.tutorials.edit') : t('admin.tutorials.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.tutorials.title')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('admin.tutorials.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('admin.tutorials.category')}
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  required
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('admin.tutorials.level')}
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value as any })}
                  required
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.tutorials.price')}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.tutorials.discount')}
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.tutorials.priority')}
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.tutorials.active')}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={(e) => handleDrop(e, 'image')}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    onChange={(e) => handleFileSelect(e, 'image')}
                  />
                  <label htmlFor="image-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.tutorials.dragAndDropImage')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.tutorials.orClickToUploadImage')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {formData.imagePath && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.tutorials.selectedImage')}
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.imagePath}
                      onChange={(e) => setFormData({ ...formData, imagePath: e.target.value })}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={(e) => handleDrop(e, 'content')}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="content-upload"
                    onChange={(e) => handleFileSelect(e, 'content')}
                  />
                  <label htmlFor="content-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.tutorials.dragAndDropContentImages')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.tutorials.orClickToUploadContentImages')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {imageError && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {imageError}
                  </Typography>
                )}
                {formData.contentImagesPathes.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.tutorials.selectedContentImages')}
                    </Typography>
                    <Grid container spacing={2}>
                      {formData.contentImagesPathes.map((path, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={path}
                              alt={`Content image ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                            />
                            {formData.contentImagesPathes.length > 1 && (
                              <IconButton
                                onClick={() => handleDeleteContentImage(index)}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingTutorial ? t('common.save') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}; 